<template>
    <div class="account-level-view">
        <global-message
            v-if="successMessage"
            type="success"
            :value="successMessage"
        />
        <div class="account-level-view__sections">
            <!-- Account Status Section -->
            <div class="account-level-view__section">
                <div class="account-level-view__header">
                    <h2 class="common__heading bottom-gap-lg">Account Status</h2>
                </div>
                <div class="account-level-view__status">
                    <div class="account-level-view__status-item">
                        <span class="account-level-view__label">Account Type:</span>
                        <span class="account-level-view__value">{{ user.accountType || 'Free' }}</span>
                    </div>
                    <div class="account-level-view__status-item">
                        <span class="account-level-view__label">Member Since:</span>
                        <span class="account-level-view__value">{{ formattedMemberDate }}</span>
                    </div>
                    <div v-if="verificationStatusText" class="account-level-view__status-item">
                        <span class="account-level-view__label">Verification Status:</span>
                        <span class="account-level-view__verification-wrapper">
                            <span class="account-level-view__verification-badge" :class="verificationStatusClass">
                                <span v-if="isVerified" class="account-level-view__verification-checkmark">✓</span>
                                {{ verificationStatusText }}
                            </span>
                            <span v-if="isRejected" class="account-level-view__verification-tooltip">
                                Contact membership@footballaustralia.com.au for more information
                            </span>
                        </span>
                    </div>
                </div>
                <p v-if="user && user.accountType && !isPlusUser && faPlusOnSale" class="account-level-view__upgrade-text">
                    Upgrade to FA+ for just $99.00
                </p>
            </div>

            <!-- Account Features Section -->
            <div class="account-level-view__section">
                <div class="account-level-view__header">
                    <h2 class="common__heading bottom-gap-lg">FA+ Features</h2>
                </div>
                <p class="account-level-view__description" v-html="descriptionText"></p>
                <!-- FA+ Feature List for Free Users while FA+ is on sale -->
                <div v-if="!isPlusUser && faPlusOnSale" class="account-level-view__features">
                    <div class="account-level-view__feature">
                        <span class="account-level-view__feature-icon">○</span>
                        <span class="account-level-view__feature-text">FIFA WORLD CUP 2026™ Events</span>
                    </div>
                    <div class="account-level-view__feature">
                        <span class="account-level-view__feature-icon">○</span>
                        <span class="account-level-view__feature-text">VIP Event Access</span>
                    </div>
                    <div class="account-level-view__feature">
                        <span class="account-level-view__feature-icon">○</span>
                        <span class="account-level-view__feature-text">Match Ticket Discounts</span>
                    </div>
                    <div class="account-level-view__feature">
                        <span class="account-level-view__feature-icon">○</span>
                        <span class="account-level-view__feature-text">Invitation Only Benefits</span>
                    </div>
                </div>

                <!-- View Benefits and Offers Button for Plus Users -->
                <div v-if="isPlusUser" class="account-level-view__benefits-button-container">
                    <button
                        class="account-level-view__benefits-button"
                        @click="showBenefitsModal = true"
                    >
                        VIEW BENEFITS AND OFFERS
                    </button>
                </div>

                <!-- View Offers Button for Free Users -->
                <div v-if="!isPlusUser" class="account-level-view__benefits-button-container">
                    <button
                        class="account-level-view__benefits-button account-level-view__benefits-button--free"
                        @click="showOffersModal = true"
                    >
                        VIEW MY OFFERS
                    </button>
                </div>
                
                <!-- EU Membership Restriction Warning -->
                <div v-if="!isPlusUser && showEuRestrictionWarning" class="account-level-view__eu-warning">
                    <span class="account-level-view__eu-warning-icon">⚠</span>
                    <p class="account-level-view__eu-warning-text">
                        From June 19 2026 until October 1 2026, Football Australia does not allow FA+ memberships from anywhere in the European Union. Any transaction with an EU address will be cancelled with no benefits provided.
                    </p>
                </div>

                <!-- Simple FA+ Sign Up Button -->
                <div v-if="!isPlusUser && faPlusOnSale" class="shopify-product-container">
                    <button 
                        class="btn btn--secondary btn--sm bottom-gap-sm account-level-view__btn"
                        :class="{ 'btn--loading': isLoadingShopify }"
                        :disabled="isLoadingShopify"
                        @click="handleShopifyCheckout"
                    >
                        <span v-if="isLoadingShopify" class="btn__spinner"></span>
                        <span v-if="isLoadingShopify">Redirecting...</span>
                        <span v-else>Sign Up for FA+</span>
                    </button>
                </div>
            </div>
        </div>

        <!-- Comparison Section - Now outside the individual sections -->
        <div v-if="user && user.accountType && !isPlusUser" class="account-level-view__comparison-section">
            <button @click="toggleComparison" class="account-level-view__comparison-toggle">
                Explore and Compare FA+ Features
                <span class="account-level-view__toggle-icon">{{ showComparison ? '▼' : '▶' }}</span>
            </button>
            <div v-if="showComparison" class="account-level-view__comparison-table">
                <!-- Desktop Table View -->
                <table class="comparison-table-desktop">
                    <thead>
                        <tr>
                            <th>Benefit Category</th>
                            <th>Free</th>
                            <th>FA+ Paid Tier</th>
                        </tr>
                    </thead>
                    <tbody>
                        <template v-for="(benefit, index) in benefits">
                            <tr :key="`row-${index}`" class="benefit-row" @click="toggleBenefit(index)" @mouseenter="showTooltip = index" @mouseleave="showTooltip = null">
                                <td class="benefit-category">
                                    <strong>{{ benefit.category }}</strong>
                                    <div v-if="showTooltip === index" class="tooltip">
                                        {{ expandedBenefits.includes(index) ? 'Click to hide details' : 'Click to find out more' }}
                                    </div>
                                </td>
                                <td class="benefit-free">{{ benefit.free }}</td>
                                <td class="benefit-plus">{{ benefit.plus }}</td>
                            </tr>
                            <tr v-if="expandedBenefits.includes(index)" :key="`detail-${index}`" class="benefit-detail-row">
                                <td colspan="3" class="benefit-details">
                                    <div class="benefit-detail-content">
                                        <div class="benefit-detail-section">
                                            <h4>Free Tier:</h4>
                                            <p>{{ benefit.freeDetail }}</p>
                                        </div>
                                        <div class="benefit-detail-section">
                                            <h4>FA+ Tier:</h4>
                                            <p>{{ benefit.plusDetail }}</p>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        </template>
                    </tbody>
                </table>

                <!-- Mobile Card View -->
                <div class="comparison-cards-mobile">
                    <div v-for="(benefit, index) in benefits" :key="`card-${index}`" class="benefit-card" @click="toggleBenefit(index)">
                        <div class="benefit-card__header">
                            <h3 class="benefit-card__title">{{ benefit.category }}</h3>
                        </div>
                        
                        <div class="benefit-card__content">
                            <p class="benefit-card__description">{{ benefit.plus }}</p>
                            <p class="benefit-card__tap-prompt">Tap for details</p>
                        </div>

                        <div v-if="expandedBenefits.includes(index)" class="benefit-card__details">
                            <div class="benefit-card__detail-section">
                                <h4>Free Tier:</h4>
                                <p>{{ benefit.freeDetail }}</p>
                            </div>
                            <div class="benefit-card__detail-section">
                                <h4>FA+ Tier:</h4>
                                <p>{{ benefit.plusDetail }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        
        <!-- FA+ Benefits Modal -->
        <benefits-modal
            v-if="showBenefitsModal"
            :is-plus-user="isPlusUser"
            @close="showBenefitsModal = false"
        />

        <!-- Free User Offers Modal -->
        <benefits-modal
            v-if="showOffersModal"
            :is-plus-user="false"
            title="Football Account Benefits & Offers"
            @close="showOffersModal = false"
        />
    </div>
</template>

<script>
import moment from 'moment';
import GlobalMessage from '../messages/GlobalMessage.vue';
import BenefitsModal from '../modal/BenefitsModal.vue';

export default {
    name: 'AccountLevelView',
    components: {
        GlobalMessage,
        BenefitsModal
    },
    props: {
        user: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            showComparison: false,
            expandedBenefits: [],
            showTooltip: null,
            isLoadingShopify: false,
            successMessage: null,
            showBenefitsModal: false,
            showOffersModal: false,
            // FA+ is off sale until the 2026/27 membership launches; set to true to show sign-up again
            faPlusOnSale: false,
            benefits: [
                {
                    category: 'VIP Event Access',
                    free: '-',
                    plus: 'Exclusive Football Australia Events',
                    freeDetail: 'Not available for free members',
                    plusDetail: 'Invitations to FA+ exclusive events such as member days, fan forums and legacy celebrations. Early registration for major Football Australia activations including AFC Women\'s Asian Cup, Australian Championships & CommBank Socceroos & CommBank Matildas.'
                },
                {
                    category: 'Domestic & International Matches Ticket Discounts',
                    free: 'General Tickets Access (5%)',
                    plus: '15% Discount + Priority Pre-Sale Access',
                    freeDetail: 'General tickets access with 5% discount',
                    plusDetail: 'Discounted tickets (15%) for National team local matches and Australia Cup and Australian Championship events. Priority pre-sale access before public. Where applicable, special FA+ allocation in premium seating area. Football Australia will also make tickets available for international matches outside Australia when there is demand. Subject to availability'
                },
                {
                    category: 'Open Training Access',
                    free: 'Public Areas Only',
                    plus: 'FA+ Member Bay',
                    freeDetail: 'Public access areas only (subject to capacity)',
                    plusDetail: 'Skip the queue with FA+ fast-lane entry to the member bay. Best positioned seating for watching drills, warmups and access to meet select players post-training. Subject to availability'
                },
                {
                    category: 'Merchandise Discounts',
                    free: 'Discount Voucher',
                    plus: 'Seasonal Voucher + Exclusive Member Drops',
                    freeDetail: 'Basic discount voucher available',
                    plusDetail: 'A seasonal merchandise voucher (one per year, per member) to use on the online store. Member-only product drops and early access to new kit releases.'
                },
                {
                    category: 'Exclusive Content',
                    free: 'Basic Newsletters',
                    plus: 'Behind-the-Scenes Access',
                    freeDetail: 'Basic newsletters and match updates',
                    plusDetail: 'Early access to behind-the-scenes content. Member-only interviews, features, and training footage. Priority invitations for future fan engagement initiatives.'
                },
                {
                    category: 'Partner Discounts',
                    free: 'Limited Public offers',
                    plus: 'Exclusive Offers',
                    freeDetail: 'Limited access to public partner offers (generic discounts & promotions)',
                    plusDetail: 'Access to exclusive FA+ partner offers and competitions. Stay tuned for more.'
                }
            ]
        }
    },
    computed: {
        formattedMemberDate() {
            if (!this.user.plusAccountCreatedAt) return 'N/A';
            return moment(this.user.plusAccountCreatedAt).format('DD MMMM YYYY');
        },
        isPlusUser() {
            return this.user &&
                this.user.accountType &&
                (this.user.accountType === 'Plus' || this.user.accountType === 'Plus+');
        },
        descriptionText() {
            const comingSoon = '2026/27 FA+ membership coming soon.';
            if (this.isPlusUser) {
                const benefits = 'Benefits &amp; Offers for 2025/26 Members still available, select VIEW BENEFITS button below';
                return this.faPlusOnSale ? benefits : `${benefits}<br>${comingSoon}`;
            }
            return this.faPlusOnSale
                ? 'Upgrade to FA+ <strong>$99.00</strong><br>12 months'
                : comingSoon;
        },
        verificationStatus() {
            return this.user && this.user.id_verified ? this.user.id_verified : null;
        },
        isVerified() {
            return this.verificationStatus === 'approved_manual' || this.verificationStatus === 'approved';
        },
        isPending() {
            return this.verificationStatus === 'pending';
        },
        isRejected() {
            return this.verificationStatus === 'rejected';
        },
        verificationStatusText() {
            if (this.isVerified) {
                return 'ID Verified';
            } else if (this.isPending) {
                return 'Verification Pending';
            } else if (this.isRejected) {
                return 'Rejected';
            }
            return null;
        },
        showEuRestrictionWarning() {
            const now = new Date();
            const start = new Date('2026-06-18T00:00:00');
            const end = new Date('2026-10-01T00:00:00');
            return now >= start && now < end;
        },
        verificationStatusClass() {
            if (this.isVerified) {
                return 'verification-status--verified';
            } else if (this.isPending) {
                return 'verification-status--pending';
            } else if (this.isRejected) {
                return 'verification-status--rejected';
            }
            return '';
        }
    },
    methods: {
        toggleComparison() {
            this.showComparison = !this.showComparison;
        },
        toggleBenefit(index) {
            const expandedIndex = this.expandedBenefits.indexOf(index);
            if (expandedIndex > -1) {
                this.expandedBenefits.splice(expandedIndex, 1);
            } else {
                this.expandedBenefits.push(index);
            }
        },
        handlePaymentRedirect() {
            // Check for Shopify payment success/failure on page load
            const urlParams = new URLSearchParams(window.location.search);
            const shopifySuccess = urlParams.get('shopify_success');
            const shopifyError = urlParams.get('shopify_error');
            const cartId = urlParams.get('cart_id');
            
            if (shopifySuccess === 'true') {
                // Show success message and refresh user data
                this.showSuccessMessage('Payment successful! Your FA+ membership is now active.');
                this.$store.dispatch('user/RETRIEVE_USER_DATA');
            } else if (shopifyError === 'true') {
                // Show error message
                this.showError('Payment was not completed. Please try again.');
            }
            
            // Clean up URL parameters
            if (shopifySuccess || shopifyError) {
                window.history.replaceState({}, document.title, window.location.pathname);
            }
        },
        showSuccessMessage(message) {
            this.successMessage = message;
        },
        async handleShopifyCheckout() {
            try {
                this.isLoadingShopify = true;
                console.log('Creating Shopify cart via CRM API...');
                
                // Get current user from store
                const user = this.$store.state.user.user;
                
                if (!user || !user.email) {
                    this.isLoadingShopify = false;
                    alert('Please log in to continue with checkout.');
                    return;
                }
                
                // Get Cognito ID (username is the Cognito sub ID)
                const cognitoId = user.username;
                console.log('Cognito ID:', cognitoId);
                console.log('User email:', user.email);
                
                // Create cart via CRM API (following same pattern as other API calls)
                const cartData = await this.$store.dispatch('user/CREATE_SHOPIFY_CART', {
                    cognitoId: cognitoId,
                    email: user.email
                });

                if (window.fbq) {
                    window.fbq('track', 'InitiateCheckout');
                    console.debug('Facebook Pixel InitiateCheckout tracked.');
                } else {
                    console.warn('Facebook Pixel not found.');
                }
                
                if (cartData && cartData.checkoutUrl) {
                    console.log('Cart created successfully:', cartData);
                    console.log('Opening checkout:', cartData.checkoutUrl);

                    window.location.href = cartData.checkoutUrl;
                } else {
                    throw new Error('Failed to create cart');
                }
                
            } catch (error) {
                this.isLoadingShopify = false;
                console.error('Shopify cart creation error:', error);
                alert('Unable to create cart. Please try again.');
            }
        },
    },
    mounted() {
        // Check for payment redirect on component mount
        this.handlePaymentRedirect();
        if (window.fbq) {
            window.fbq('track', 'PageView');
            console.debug('Facebook Pixel PageView tracked.');
        } else {
            console.warn('Facebook Pixel not found.');
        }
    }
}
</script>

<style lang="scss" scoped>
    @import '~sass/_variables.scss';
    @import "~sass/_config.scss";
    @import "~sass/mixins/_media-query.scss";

    .account-level-view {
        font-family: $font-1;

        &__sections {
            display: grid;
            gap: 30px;
        }

        &__section {
            background: #f8f9fa;
            border: 1px solid #e9ecef;
            border-radius: 8px;
            padding: 20px;
        }

        &__status {
            margin-bottom: 20px;
        }

        &__status-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 12px;
            padding: 8px 0;
            border-bottom: 1px solid #e9ecef;

            &:last-child {
                border-bottom: none;
                margin-bottom: 0;
            }
        }

        &__features {
            margin-bottom: 20px;
        }

        &__feature {
            display: flex;
            align-items: center;
            margin-bottom: 8px;

            &:last-child {
                margin-bottom: 0;
            }
        }

        &__feature-icon {
            color: #28a745;
            font-weight: bold;
            margin-right: 8px;
            font-size: 16px;
        }

        &__feature-text {
            font-size: 14px;
            color: $col-neutral--7;
        }

        &__description {
            font-size: 14px;
            line-height: 1.5;
            color: $col-neutral--6;
            margin-bottom: 16px;
        }

        &__upgrade-text {
            font-size: 16px;
            color: #007bff;
            font-weight: 600;
            margin-top: 12px;
            text-align: center;
        }

        &__comparison-section {
            margin-top: 30px;
            text-align: center;
        }

        &__comparison-toggle {
            background: none;
            border: none;
            color: #007bff;
            padding: 0;
            cursor: pointer;
            font-size: 16px;
            font-weight: 500;
            transition: all 0.3s ease;
            display: inline-flex;
            align-items: center;
            gap: 8px;

            &:hover {
                color: #0056b3;
            }
        }

        &__toggle-icon {
            font-size: 12px;
        }

        &__comparison-table {
            margin-top: 16px;

            // Desktop Table View
            .comparison-table-desktop {
                width: 100%;
                border-collapse: collapse;
                font-size: 14px;
                display: table;

                @include maxq(tablet) {
                    display: none; // Hide table on mobile
                }

                th, td {
                    padding: 12px;
                    text-align: left;
                    border-bottom: 1px solid #e9ecef;
                    vertical-align: top;
                }

                th {
                    background: #f8f9fa;
                    font-weight: 600;
                    color: #495057;
                    position: sticky;
                    top: 0;
                }

                td {
                    color: #6c757d;
                    line-height: 1.4;
                }

                .benefit-row {
                    cursor: pointer;
                    transition: background-color 0.2s ease;

                    &:hover {
                        background-color: #f8f9fa;
                    }
                }

                .benefit-category {
                    font-weight: 500;
                    color: #495057;
                    width: 30%;
                    position: relative;
                    word-break: break-word;
                }

                .tooltip {
                    position: absolute;
                    top: -35px;
                    left: 0;
                    background: #333;
                    color: white;
                    padding: 8px 12px;
                    border-radius: 4px;
                    font-size: 12px;
                    white-space: nowrap;
                    z-index: 1000;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
                    
                    &::after {
                        content: '';
                        position: absolute;
                        top: 100%;
                        left: 20px;
                        border: 5px solid transparent;
                        border-top-color: #333;
                    }
                }

                .benefit-free {
                    width: 25%;
                    font-size: 13px;
                    word-break: break-word;
                }

                .benefit-plus {
                    width: 45%;
                    font-size: 13px;
                    color: #28a745;
                    font-weight: 500;
                    word-break: break-word;
                }

                .benefit-detail-row {
                    background-color: #f8f9fa;
                    
                    .benefit-details {
                        padding: 0;
                        border-top: none;
                    }
                }

                .benefit-detail-content {
                    padding: 16px;
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 20px;
                }

                .benefit-detail-section {
                    h4 {
                        font-size: 14px;
                        font-weight: 600;
                        color: #495057;
                        margin: 0 0 8px 0;
                    }

                    p {
                        font-size: 13px;
                        line-height: 1.5;
                        color: #6c757d;
                        margin: 0;
                    }
                }
            }

            // Mobile Card View
            .comparison-cards-mobile {
                display: none;

                @include maxq(tablet) {
                    display: block; // Show cards on mobile
                }

                .benefit-card {
                    background: #fff;
                    border: 1px solid #e9ecef;
                    border-radius: 8px;
                    margin-bottom: 16px;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    overflow: hidden;

                    &:hover {
                        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                        transform: translateY(-2px);
                    }

                    &__header {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        padding: 16px;
                        background: #f8f9fa;
                        border-bottom: 1px solid #e9ecef;
                    }

                    &__title {
                        font-size: 16px;
                        font-weight: 600;
                        color: #495057;
                        margin: 0;
                        flex: 1;
                        line-height: 1.3;
                    }

                    &__expand-icon {
                        color: #007bff;
                        font-size: 14px;
                        font-weight: bold;
                        margin-left: 12px;
                    }

                    &__content {
                        padding: 16px;
                    }

                    &__description {
                        font-size: 14px;
                        line-height: 1.5;
                        color: #6c757d;
                        margin: 0 0 12px 0;
                        word-break: break-word;
                    }

                    &__tap-prompt {
                        font-size: 12px;
                        color: #007bff;
                        margin: 0;
                        font-weight: 500;
                        text-align: center;
                        opacity: 0.8;
                    }

                    &__details {
                        padding: 16px;
                        background: #f8f9fa;
                        border-top: 1px solid #e9ecef;
                    }

                    &__detail-section {
                        margin-bottom: 16px;

                        &:last-child {
                            margin-bottom: 0;
                        }

                        h4 {
                            font-size: 14px;
                            font-weight: 600;
                            color: #495057;
                            margin: 0 0 8px 0;
                        }

                        p {
                            font-size: 13px;
                            line-height: 1.5;
                            color: #6c757d;
                            margin: 0;
                        }
                    }
                }
            }
        }

        &__btn {
            transition: all 0.3s ease;
            
            &:hover {
                transform: translateY(-1px);
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
            }
        }

        &__eu-warning {
            display: flex;
            align-items: flex-start;
            gap: 10px;
            margin-top: 20px;
            padding: 12px 16px;
            background-color: #fff8e1;
            border: 1px solid #f9a825;
            border-left: 4px solid #f9a825;
            border-radius: 4px;
        }

        &__eu-warning-icon {
            font-size: 18px;
            color: #f9a825;
            flex-shrink: 0;
            line-height: 1.4;
        }

        &__eu-warning-text {
            font-size: 13px;
            line-height: 1.5;
            color: #5f4300;
            margin: 0;
        }

        // Shopify Web Component Styling
        .shopify-product-container {
            margin-top: 20px;
            
            // Button loading state styling
            .btn--loading {
                position: relative;
                pointer-events: none;
                opacity: 0.8;
                
                .btn__spinner {
                    display: inline-block;
                    width: 16px;
                    height: 16px;
                    margin-right: 8px;
                    border: 2px solid transparent;
                    border-top: 2px solid currentColor;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                }
            }
            
            @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
            }
            
            // Style the Shopify product component to match your design
            ::deep shopify-product {
                display: block;
                
                .product {
                    border: 1px solid #e9ecef;
                    border-radius: 8px;
                    padding: 20px;
                    background: #fff;
                    transition: all 0.3s ease;
                    
                    &:hover {
                        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                        transform: translateY(-2px);
                    }
                }
                
                .product__title {
                    font-size: 18px;
                    font-weight: 600;
                    color: #495057;
                    margin-bottom: 8px;
                }
                
                .product__price {
                    font-size: 24px;
                    font-weight: 700;
                    color: #28a745;
                    margin-bottom: 16px;
                }
                
                .product__buy-button {
                    width: 100%;
                    padding: 12px 24px;
                    background: #007bff;
                    color: white;
                    border: none;
                    border-radius: 6px;
                    font-size: 16px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    
                    &:hover {
                        background: #0056b3;
                        transform: translateY(-1px);
                        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
                    }
                    
                    &:disabled {
                        opacity: 0.6;
                        cursor: not-allowed;
                        transform: none;
                        box-shadow: none;
                    }
                }
            }
        }

        &__label {
            font-size: 14px;
            line-height: 16px;
            color: $col-neutral--6;
            font-weight: 500;
        }

        &__value {
            font-size: 16px;
            line-height: 20px;
            color: $col-neutral--8;
            font-weight: 600;
        }

        &__verification-badge {
            display: inline-flex;
            align-items: center;
            padding: 6px 16px;
            border-radius: 20px;
            font-size: 14px;
            font-weight: 600;

            &.verification-status--verified {
                color: #28a745;
                background-color: transparent;
                border: 2px solid #28a745;
            }

            &.verification-status--pending {
                color: $col-warn--2;
                background-color: transparent;
                border: 2px solid $col-warn--2;
            }

            &.verification-status--rejected {
                color: #dc3545;
                background-color: transparent;
                border: 2px solid #dc3545;
                cursor: help;
            }
        }

        &__verification-checkmark {
            margin-right: 6px;
            font-weight: bold;
        }

        &__verification-wrapper {
            position: relative;
            display: inline-flex;
            align-items: center;

            &:hover .account-level-view__verification-tooltip {
                visibility: visible;
                opacity: 1;
            }
        }

        &__verification-tooltip {
            visibility: hidden;
            opacity: 0;
            position: absolute;
            top: calc(100% + 8px);
            right: 0;
            background: #333;
            color: white;
            padding: 10px 14px;
            border-radius: 6px;
            font-size: 13px;
            font-weight: 400;
            white-space: nowrap;
            z-index: 1000;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
            transition: opacity 0.2s ease, visibility 0.2s ease;

            &::before {
                content: '';
                position: absolute;
                bottom: 100%;
                right: 20px;
                border: 6px solid transparent;
                border-bottom-color: #333;
            }
        }

        &__benefits-button-container {
            margin-top: 20px;
        }

        &__benefits-button {
            width: 100%;
            padding: 14px 24px;
            background-color: #2B0A5E;
            color: white;
            border: none;
            border-radius: 4px;
            font-size: 14px;
            font-weight: 700;
            letter-spacing: 0.5px;
            cursor: pointer;
            transition: background-color 0.2s ease;
            text-transform: uppercase;

            &:hover {
                background-color: #1F073F;
            }

            &:active {
                transform: translateY(1px);
            }

            &--free {
                background-color: #007bff;

                &:hover {
                    background-color: #0056b3;
                }
            }
        }

        @include mq(largeTablet) {
            &__sections {
                grid-template-columns: 1fr 1fr;
                gap: 40px;
            }

            &__header {
                display: flex;
                align-items: flex-start;
            }

            &__btn {
                margin-left: 5px;

                &:first-of-type {
                    margin-left: auto;
                }
            }

            &__label {
                font-size: 16px;
                line-height: 19px;
            }

            &__value {
                font-size: 18px;
                line-height: 22px;
            }

            &__description {
                font-size: 16px;
            }

            &__feature-text {
                font-size: 16px;
            }
        }
    }
</style>
